import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PartyPopper, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [amountText, setAmountText] = useState("");
  const [kind, setKind] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.application) { setFirstName(data.application.first_name || ""); setEmail(data.application.email || ""); }
        if (data.kind) setKind(data.kind);
        if (typeof data.amount === "number") {
          const sym = (data.currency || "gbp").toLowerCase() === "gbp" ? "£" : "";
          setAmountText(`${sym}${(data.amount / 100).toFixed(2)}`);
        }
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
        tries.current += 1;
        if (tries.current > 20) { setState("pending"); return; }
        setTimeout(poll, 2000);
      } catch {
        tries.current += 1;
        if (tries.current > 20) { setState("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16 bg-slate-50" data-testid="payment-success-page">
      <div className="max-w-lg w-full text-center bg-white rounded-3xl border border-slate-200 p-10 shadow-sm">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-[#0F2744] animate-spin mx-auto mb-5" />
            <h1 className="text-2xl font-bold text-[#0F2744]">Confirming your payment…</h1>
            <p className="mt-3 text-slate-600">Just a moment while we confirm your membership payment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="inline-flex h-16 w-16 rounded-full bg-green-100 items-center justify-center mb-5">
              <PartyPopper className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-[#0F2744]" data-testid="payment-success-title">Payment received — you're in!</h1>
            <p className="mt-4 text-slate-600">Thanks {firstName || "there"} — {kind === "renewal" ? `your ${amountText || "membership"} renewal is` : `your ${amountText ? amountText + " " : ""}${kind === "join" ? "joining fee is" : "membership payment is"}`} paid.
              {email ? ` We've emailed a confirmation to ${email}.` : ""} A committee member will be in touch soon to arrange your first steps.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate("/events")} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="payment-see-events">See upcoming events</Button>
              <Button onClick={() => navigate("/")} variant="outline" data-testid="payment-home">Back to home</Button>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="h-10 w-10 text-amber-500 animate-spin mx-auto mb-5" />
            <h1 className="text-2xl font-bold text-[#0F2744]">Your payment is processing</h1>
            <p className="mt-3 text-slate-600">This is taking a little longer than usual. Your payment may still complete — we'll email you once it's confirmed. If you're unsure, please contact us.</p>
            <Button asChild variant="outline" className="mt-6"><Link to="/contact" data-testid="payment-contact">Contact us</Link></Button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="inline-flex h-16 w-16 rounded-full bg-red-100 items-center justify-center mb-5">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#0F2744]">We couldn't confirm your payment</h1>
            <p className="mt-3 text-slate-600">Something went wrong confirming your payment. You haven't been charged if the payment didn't complete. Please try again or get in touch.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate("/join")} className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid="payment-retry">Try again</Button>
              <Button asChild variant="outline"><Link to="/contact">Contact us</Link></Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
